// HomePage.js - Home page with integrated petition search
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import LiveActivity from './LiveActivity';
import { CalendarCheck, TrendingUp, ArrowRight, Award, Search, Landmark, ExternalLink, ArrowUp, ArrowDown } from 'lucide-react';
import { fetchPopularPetitions, clearPopularPetitionsCache, fetchPetitionData, fetchAllOpenPetitions, fetchHomeDebates, fetchHomeDebated, fetchHomePopular, fetchPopularConstituencies, fetchHomeStats } from '../services/petitionService';
import { getDebateTarget, LIVE_WINDOW } from '../utils/debateTime';
import { getMpPhotos, normaliseMpName } from '../services/mpPhotoService';
import { extractPetitionId, buildPetitionUrl, slugify, buildAppPetitionUrl } from '../utils/urlUtils';

// Import components
import Header from './Header';
import PetitionCard from './PetitionCard';
import PetitionFilters from './PetitionFilters';
import Pagination from './Pagination';

// Shared styles & theme tokens
import { PageHeader, PageTitle, PageSubtitle } from '../styles/shared';
import { colors, fonts, breakpoints, spacing } from '../styles/theme';

// Custom hook for managing document meta tags
const useDocumentMeta = (title, description) => {
  useEffect(() => {
    // Update document title
    if (title) {
      document.title = title;
    }

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription && description) {
      metaDescription.setAttribute('content', description);
    }

    // Update OG tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle && title) {
      ogTitle.setAttribute('content', title);
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription && description) {
      ogDescription.setAttribute('content', description);
    }

    // Update Twitter tags
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle && title) {
      twitterTitle.setAttribute('content', title);
    }

    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription && description) {
      twitterDescription.setAttribute('content', description);
    }
  }, [title, description]);
};

// ============= STYLED COMPONENTS =============
// PageWrapper keeps unique rotating background animation — local only
const PageWrapper = styled.div`
  min-height: 100vh;
  background: ${colors.bgGradient};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    animation: rotate 30s linear infinite;
  }

  @keyframes rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

// Container is full-width on home page (different from shared max-width: 1400px)
const HomeContainer = styled.main`
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  padding: ${spacing.pageTopDesktop} ${spacing.pageSideDesktop} ${spacing.xl};
  position: relative;
  z-index: 1;
  box-sizing: border-box;

  /* Tighten the shared PageHeader bottom margin on home */
  & > header {
    margin-bottom: 24px;
  }

  @media (min-width: 1600px) {
    padding: ${spacing.pageTopDesktop} 60px ${spacing.xl};
  }

  @media (max-width: ${breakpoints.mobile}) {
    padding: ${spacing.pageTopMobile} ${spacing.pageSideMobile} ${spacing.lg};
  }
`;

// HeroSection uses shared PageHeader (same semantics, slightly less bottom margin)
// PageTitle and PageSubtitle imported from shared

// ============= ENHANCED SEARCH FORM COMPONENT =============
const SearchContainer = styled.form`
  max-width: 690px;
  width: 100%;
  margin: 0 auto 14px;
  position: relative;

  @media (max-width: 640px) { margin-bottom: 20px; }
`;

const InputWrapper = styled.div`
  position: relative;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:focus-within {
    box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px;
    &:focus-within {
      transform: none;
    }
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 20px 180px 20px 55px; // Increased right padding for clear button
  font-size: 1.1em;
  border: none;
  background: transparent;
  color: #333;
  border-radius: 16px;
  outline: none;

  &::placeholder {
    color: #999;
  }

  @media (max-width: 768px) {
    padding: 14px 14px 14px 45px;
    font-size: 16px;
    box-sizing: border-box;
  }
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  font-size: 1.2em;
  pointer-events: none;

  @media (max-width: 768px) {
    top: 34px;
  }
`;

const SubmitButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 1em;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover:not(:disabled) {
    transform: translateY(-50%) scale(1.05);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    position: static;
    transform: none;
    width: 100%;
    padding: 14px 16px;
    font-size: 13px;
    border-radius: 12px;
    justify-content: center;
    box-sizing: border-box;

    &:hover:not(:disabled) {
      transform: none;
    }
  }
`;

const SearchResults = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  max-height: 420px;
  overflow-y: auto;
  z-index: 200;
  animation: slideDown 0.2s ease;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ResultItem = styled(Link)`
  width: 100%;
  padding: 16px 20px;
  text-align: left;
  background: white;
  border: none;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background 0.2s ease;
  display: block;
  text-decoration: none;
  color: inherit;

  &:hover {
    background: #f8f9fa;
  }

  &:first-child {
    border-radius: 12px 12px 0 0;
  }

  &:last-child {
    border-bottom: none;
    border-radius: 0 0 12px 12px;
  }

  .title {
    font-size: 0.95em;
    font-weight: 600;
    color: #333;
    margin-bottom: 4px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .meta {
    font-size: 0.85em;
    color: #666;
    display: flex;
    gap: 15px;
    align-items: center;
    flex-wrap: wrap;
  }

  .signatures {
    color: #667eea;
    font-weight: 500;
  }

  .status {
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 0.75em;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    
    &.responded {
      background: #10b981;
      color: white;
    }
    
    &.debated {
      background: #f59e0b;
      color: white;
    }
  }

  .department {
    padding: 2px 8px;
    background: #e5e7eb;
    border-radius: 4px;
    font-size: 0.75em;
    font-weight: 500;
  }
`;

const SearchingIndicator = styled.div`
  padding: 20px;
  text-align: center;
  color: #666;
  font-size: 0.9em;

  .spinner {
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 2px solid #f0f0f0;
    border-top: 2px solid #667eea;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin-right: 8px;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const NoResults = styled.div`
  padding: 20px;
  text-align: center;
  color: #666;
  font-size: 0.9em;
`;

const InputHelper = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9em;
  margin-top: 10px;
  margin-bottom: -20px;
  line-height: 1.5;
`;


const ErrorMessage = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: white;
  padding: 16px 24px;
  border-radius: 12px;
  margin: 20px auto;
  max-width: 600px;
  text-align: center;
  font-size: 0.95em;
  role: alert;
  aria-live: polite;
`;

const PopularSection = styled.section`
  max-width: 1200px;
  margin: 0 auto;
`;

const LiveIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.4em;
  background: rgba(16, 185, 129, 0.2);
  padding: 8px 16px;
  border-radius: 20px;
  border: 1px solid rgba(16, 185, 129, 0.3);
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  aria-label: "Live petition data";

  .dot {
    width: 10px;
    height: 10px;
    background: #10b981;
    border-radius: 50%;
    position: relative;
  }

  .dot::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: #10b981;
    pointer-events: none;
    will-change: transform, opacity;
    animation: pulse-ring 2s ease-out infinite;
  }

  @keyframes pulse-ring {
    0%   { transform: scale(1);   opacity: 0.7; }
    100% { transform: scale(2.8); opacity: 0;   }
  }
`;

const TabContainer = styled.nav`
  display: flex;
  justify-content: center;
  gap: 4px;
  margin-bottom: 40px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 4px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
  role: tablist;
`;

const TabButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 1em;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.active ? 'rgba(255, 255, 255, 0.95)' : 'transparent'};
  color: ${props => props.active ? '#667eea' : 'rgba(255, 255, 255, 0.8)'};
  text-transform: capitalize;
  position: relative;
  role: tab;
  aria-selected: ${props => props.active};
  aria-controls: ${props => `${props.id}-panel`};

  &:hover:not(:disabled) {
    background: ${props => props.active ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.1)'};
    color: ${props => props.active ? '#667eea' : 'white'};
  }

  ${props => props.active && `
    box-shadow: 0 4px 12px rgba(255, 255, 255, 0.2);
  `}
`;

const SectionTitle = styled.h3`
  text-align: center;
  font-size: 2em;
  font-weight: 600;
  color: white;
  margin-bottom: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  .live-dot {
    width: 10px;
    height: 10px;
    background: ${props => props.status === 'closed' ? '#ef4444' : '#10B981'};
    border-radius: 50%;
    position: relative;
    aria-hidden: true;
  }

  .live-dot::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: ${props => props.status === 'closed' ? '#ef4444' : '#10B981'};
    pointer-events: none;
    will-change: transform, opacity;
    animation: ${props => props.status === 'closed' ? 'none' : 'pulse-ring 2s ease-out infinite'};
  }

  @keyframes pulse-ring {
    0%   { transform: scale(1);   opacity: 0.7; }
    100% { transform: scale(2.8); opacity: 0;   }
  }
`;

const ViewAllButton = styled.a`
  background: rgba(255, 255, 255, 0.15);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 12px 24px;
  border-radius: 25px;
  cursor: pointer;
  font-size: 0.9em;
  font-weight: 600;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 20px auto;
  text-decoration: none;
  width: fit-content;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 255, 255, 0.2);
  }

  .arrow {
    font-size: 1em;
    aria-hidden: true;
  }
`;

const ResultsInfo = styled.p`
  text-align: center;
  color: white;
  margin-bottom: 20px;
  font-size: 0.9em;
  opacity: 0.9;
  aria-live: polite;
  aria-atomic: true;
`;

const PetitionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
  max-width: 1200px;
  margin: 0 auto;
  role: list;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
  
  @media (min-width: 769px) and (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 1025px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 60px;
  color: white;
  role: status;
  aria-live: polite;
  
  .spinner {
    display: inline-block;
    width: 24px;
    height: 24px;
    border: 3px solid rgba(255, 255, 255, 0.3);
    border-top: 3px solid white;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-right: 12px;
    aria-hidden: true;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const SEOContent = styled.div`
  position: absolute;
  left: -10000px;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;

const ConstituencySection = styled.section`
  max-width: 980px;
  width: 100%;
  margin: 32px auto 0;
  padding: 0 16px;
`;

const ConstituencySectionHeader = styled.div`
  text-align: center;
  margin-bottom: 16px;

  h3 {
    font-size: 1em;
    font-weight: 700;
    color: rgba(255,255,255,0.9);
    margin: 0 0 4px;
    letter-spacing: -0.2px;
  }

  .cs-sub {
    font-size: 0.78em;
    color: rgba(255,255,255,0.45);
    margin: 0;
  }
`;

const ConstituencyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 10px;

  @media (max-width: 900px) { grid-template-columns: repeat(4, 1fr); }
  @media (max-width: 560px) { grid-template-columns: repeat(3, 1fr); }
`;

const ConstituencyExploreBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 18px auto 0;
  padding: 8px 20px;
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.18);
  border-radius: 20px;
  color: rgba(255,255,255,0.75);
  font-size: 0.82em;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.15s;

  &:hover {
    background: rgba(255,255,255,0.18);
    color: #fff;
    transform: translateY(-1px);
  }
`;

const ConstituencyCard = styled(Link)`
  background: rgba(255,255,255,0.07);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 12px;
  padding: 14px 10px;
  cursor: pointer;
  text-align: center;
  text-decoration: none;
  transition: all 0.18s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;

  &:hover {
    background: rgba(255,255,255,0.13);
    border-color: rgba(255,255,255,0.22);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0,0,0,0.2);
  }

  img {
    width: 46px;
    height: 46px;
    border-radius: 50%;
    object-fit: cover;
    object-position: top center;
    border: 2px solid rgba(255,255,255,0.2);
  }

  .cc-name {
    font-size: 0.72em;
    font-weight: 700;
    color: white;
    line-height: 1.3;
  }

  .cc-mp {
    font-size: 0.65em;
    color: rgba(255,255,255,0.45);
    line-height: 1.2;
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 140px; // Position it to the left of the submit button
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: #999;
  font-size: 1.2em;
  cursor: pointer;
  padding: 8px;
  display: ${props => props.show ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  width: 32px;
  height: 32px;
  border-radius: 50%;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #666;
  }

  &:focus {
    outline: none;
    background: rgba(0, 0, 0, 0.05);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

// ============= HERO STATS STRIP =============
/* Sits beside the open-petitions figure. Counts petitions that opened today,
   which is not the same as the net change in the total — some close too. */
const DeltaChip = styled.span`
  display: inline-block;
  margin-left: 7px;
  padding: 2px 7px;
  border-radius: 20px;
  /* Zero is shown rather than hidden so every state tile keeps the same
     shape; it reads as "nothing today", not as an absent figure. Green
     would imply movement, so a zero day is neutral. */
  background: ${p => (p.$zero ? 'rgba(148, 163, 184, 0.14)' : 'rgba(34, 197, 94, 0.16)')};
  border: 1px solid ${p => (p.$zero ? 'rgba(148, 163, 184, 0.26)' : 'rgba(34, 197, 94, 0.32)')};
  color: ${p => (p.$zero ? 'rgba(203, 213, 225, 0.75)' : '#4ade80')};
  font-size: 0.42em;
  font-weight: 700;
  letter-spacing: 0.2px;
  vertical-align: middle;
  white-space: nowrap;

  @media (max-width: 640px) { font-size: 0.4em; margin-left: 5px; padding: 2px 6px; }
`;

const HeroStats = styled.div`
  display: flex;
  justify-content: center;
  gap: 0;
  flex-wrap: wrap;
  /* Wide enough for the longest label ("Rejected petitions") to sit on one
     line; at 760px it wrapped while its neighbours did not. */
  max-width: 880px;
  margin: 14px auto 24px;

  @media (max-width: 640px) { margin: 20px auto 28px; }
  background: rgba(255,255,255,0.07);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 14px;
  overflow: hidden;
`;

const HeroStat = styled.div`
  flex: 1;
  min-width: 100px;
  text-align: center;
  padding: 14px 18px;
  border-right: 1px solid rgba(255,255,255,0.08);
  &:last-child { border-right: none; }

  display: flex;
  flex-direction: column;
  align-items: center;

  .hs-val {
    font-size: 1.25em;
    font-weight: 700;
    color: #fff;
    letter-spacing: -0.5px;
    line-height: 1;
    /* Number and chip are separate rows, so a wide number can never push the
       chip onto a line of its own in one tile but not another. */
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    /* Reserve the chip row on every tile — number 20px + gap 5px + chip 19px.
       Tiles without a chip would otherwise pull their label up and the five
       labels would not share a baseline. */
    min-height: 44px;
    justify-content: flex-start;
  }
  .hs-lbl {
    font-size: 0.65em;
    color: rgba(255,255,255,0.45);
    text-transform: uppercase;
    letter-spacing: 0.7px;
    margin-top: 6px;
    /* Pinned to the bottom so a missing chip does not raise the label. */
    margin-bottom: 0;
  }
`;


/* The open-petitions tile is the one stat with an obvious destination, so it
   is a link. Kept as a separate component rather than converting HeroStat:
   the other three tiles have nowhere to go and must stay inert. */
const HeroStatLink = styled(HeroStat).attrs({ as: Link })`
  display: block;
  text-decoration: none;
  color: inherit;
  background: transparent;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(255,255,255,0.06);
  }
  &:hover .hs-val { color: #fff; }
  &:hover .hs-lbl { color: rgba(255,255,255,0.7); }

  &:focus-visible {
    outline: 2px solid rgba(255,255,255,0.5);
    outline-offset: -2px;
  }
`;

// ============= QUICK NAV CHIPS =============
const QuickNav = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
  margin: 0 auto 28px;
  max-width: 860px;
`;

const QuickChip = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border-radius: 20px;
  font-size: 0.82em;
  font-weight: 600;
  color: rgba(255,255,255,0.82);
  background: rgba(255,255,255,0.09);
  border: 1px solid rgba(255,255,255,0.14);
  text-decoration: none;
  transition: all 0.15s ease;
  white-space: nowrap;

  svg { width: 13px; height: 13px; opacity: 0.8; }

  &:hover {
    background: rgba(255,255,255,0.18);
    color: #fff;
    border-color: rgba(255,255,255,0.3);
    transform: translateY(-1px);
  }
`;

// ============= SEARCH HINT CHIPS =============
const SearchHints = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 10px;

  @media (max-width: 640px) { margin-top: 0; }
`;

const HintLabel = styled.span`
  font-size: 0.78em;
  color: rgba(255,255,255,0.45);
`;

const HintChip = styled.button`
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.15);
  color: rgba(255,255,255,0.75);
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75em;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;

  &:hover {
    background: rgba(255,255,255,0.18);
    color: #fff;
  }
`;

// ============= DEBATE COUNTDOWN HELPERS =============
// Debate timing lives in utils/debateTime.js so every view agrees.

function padN(n) { return String(n).padStart(2, '0'); }

function formatCountdown(ms) {
  if (ms <= 0) return null;
  const s = Math.floor(ms / 1000);
  const days = Math.floor(s / 86400);
  const hrs = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const secs = s % 60;
  if (days > 0) return `${days}d ${padN(hrs)}:${padN(mins)}:${padN(secs)}`;
  return `${padN(hrs)}:${padN(mins)}:${padN(secs)}`;
}

const MiniCountdown = ({ dateStr }) => {
  const [diff, setDiff] = useState(() => {
    const t = getDebateTarget(dateStr);
    return t ? t - Date.now() : null;
  });

  useEffect(() => {
    const target = getDebateTarget(dateStr);
    if (!target) return;
    const id = setInterval(() => setDiff(target - Date.now()), 1000);
    return () => clearInterval(id);
  }, [dateStr]);

    if (!diff) return null;

  if (diff <= 0 && diff > -LIVE_WINDOW) {
    return (
      <CountdownText urgent>
        <span className="cd-label" style={{ color: '#f87171' }}>🔴 Debate Live Now</span>
      </CountdownText>
    );
  }
  if (diff <= -LIVE_WINDOW) return null;

  const isUrgent = diff < 2 * 86400 * 1000;

  const totalSecs = Math.floor(diff / 1000);
  const days  = Math.floor(totalSecs / 86400);
  const hrs   = Math.floor((totalSecs % 86400) / 3600);
  const mins  = Math.floor((totalSecs % 3600) / 60);
  const secs  = totalSecs % 60;

  const units = [
    { val: days, max: 30,  label: 'Days' },
    { val: hrs,  max: 24,  label: 'Hrs' },
    { val: mins, max: 60,  label: 'Min' },
    { val: secs, max: 60,  label: 'Sec' },
  ];

  return (
    <CountdownText urgent={isUrgent}>
      <span className="cd-label">Debate starts in</span>
      <span className="cd-values">
        <span className="cd-block"><strong>{padN(days)}</strong><em>{days === 1 ? 'Day' : 'Days'}</em></span>
        <span className="cd-sep">:</span>
        <span className="cd-block"><strong>{padN(hrs)}</strong><em>Hr</em></span>
        <span className="cd-sep">:</span>
        <span className="cd-block"><strong>{padN(mins)}</strong><em>Min</em></span>
        <span className="cd-sep">:</span>
        <span className="cd-block"><strong>{padN(secs)}</strong><em>Sec</em></span>
      </span>
    </CountdownText>
  );
};

// ============= TWO-COLUMN SECTION STYLED COMPONENTS =============
const PhaseTag = styled.span`
  flex-shrink: 0;
  padding: 1px 7px;
  border-radius: 5px;
  font-size: 0.72em;
  font-weight: 700;
  letter-spacing: 0.2px;
  background: ${p => (p.$upcoming ? 'rgba(102,126,234,0.18)' : 'rgba(148,163,184,0.16)')};
  color: ${p => (p.$upcoming ? '#a5b4fc' : '#94a3b8')};
`;

const DebatedItem = styled.div`
  border-bottom: 1px solid #cbd5e0;
  &:last-child { border-bottom: none; }

  /* The item draws its own separator, which would otherwise land between the
     petition and its resource links rather than below the pair. */
  > a { border-bottom: none; }

  /* MiniPetitionItem highlights itself on hover, but it now covers only the
     title half of the row — the rounded box cut across the links below it and
     made them read as part of the next petition. Move the highlight up to this
     wrapper so it spans the petition and its links as one row. */
  &:hover {
    background: rgba(55, 49, 81, 0.07);
    border-radius: 12px;
    border-bottom-color: transparent;
    box-shadow: inset 0 0 0 1px rgba(55, 49, 81, 0.2);
  }
  &:hover > a { background: transparent; box-shadow: none; }
`;

const DebateLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 0 16px 12px 62px;
  margin-top: -6px;

  a {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 9px;
    border-radius: 20px;
    background: #eef1f8;
    color: #4a5578;
    font-size: 0.72em;
    font-weight: 600;
    text-decoration: none;
    transition: background 0.15s, color 0.15s;
  }
  a:hover { background: #dfe4f2; color: #2b3358; }

  @media (max-width: 640px) { padding-left: 16px; }
`;

/* Rank movement against the ranking an hour ago, read like a market ticker:
   up, down, unchanged, or newly arrived on the board. */
const Move = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  font-size: 0.78em;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${p => (p.$dir === 'up' ? '#15803d' : p.$dir === 'down' ? '#b91c1c' : p.$dir === 'new' ? '#4338ca' : '#9aa0b4')};

  svg { width: 11px; height: 11px; }
`;

const RankMove = ({ $change }) => {
  // null means the petition was not on the board an hour ago.
  if ($change === null || $change === undefined) {
    return <Move $dir="new" title="New entry in the trending list">ENTRY</Move>;
  }
  if ($change > 0) {
    return (
      <Move $dir="up" title={`Up ${$change} place${$change > 1 ? 's' : ''} in the last hour`}>
        <ArrowUp aria-hidden="true" />{$change}
      </Move>
    );
  }
  if ($change < 0) {
    return (
      <Move $dir="down" title={`Down ${-$change} place${$change < -1 ? 's' : ''} in the last hour`}>
        <ArrowDown aria-hidden="true" />{-$change}
      </Move>
    );
  }
  return <Move $dir="flat" title="No change in the last hour">—</Move>;
};

const TwoColSection = styled.div`
  width: 100%;
  margin: 0 auto 24px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (min-width: 1600px) {
    gap: 28px;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

/* Popular Petitions is hidden on desktop for now — the three debate and
   trending cards fill the row there. It still shows on mobile, where cards
   stack and there is no row to fill. Delete this wrapper to restore it. */
const MobileOnlyColCard = styled.div`
  display: flex;

  > * { width: 100%; }

  @media (min-width: 641px) {
    display: none;
  }
`;

const ColCard = styled.div`
  background: rgba(255, 255, 255, 0.97);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.4);
  display: flex;
  flex-direction: column;
`;

const ColCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  background: ${p => p.variant === 'debate'
    ? '#373151'
    : p.variant === 'debated'
    ? 'linear-gradient(135deg, #2f4858 0%, #3d5a6c 100%)'
    : p.variant === 'trending'
    ? 'linear-gradient(135deg, #c2410c 0%, #9a3412 100%)'
    : 'linear-gradient(135deg, #92400e 0%, #b45309 60%, #d97706 100%)'};

  svg {
    width: 18px;
    height: 18px;
    color: rgba(255, 255, 255, 0.95);
    flex-shrink: 0;
  }

  h3 {
    color: white;
    font-size: 0.95em;
    font-weight: 700;
    margin: 0;
    letter-spacing: 0.2px;
  }

  .col-sub {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.72em;
    margin-left: 2px;
  }

  .col-badge {
    margin-left: auto;
    background: rgba(255, 255, 255, 0.22);
    color: white;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 0.75em;
    font-weight: 700;
    letter-spacing: 0.3px;
    border: 1px solid rgba(255, 255, 255, 0.3);
  }
`;

const ColCardBody = styled.div`
  padding: 4px 10px 10px;
  flex: 1;
`;

const MiniPetitionItem = styled(Link)`
  display: block;
  text-decoration: none;
  width: 100%;
  background: transparent;
  border: none;
  border-bottom: 1px solid #cbd5e0;
  padding: 10px 8px;
  cursor: pointer;
  text-align: left;
  transition: all 0.18s ease;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  border-radius: 0;
  position: relative;

  &:last-child {
    border-bottom: none;
  }

  /* CSS tooltip */

  &:hover {
    background: ${p => p.variant === 'debate'
      ? 'rgba(55,49,81,0.07)'
      : p.variant === 'trending'
      ? 'linear-gradient(135deg, rgba(194,65,12,0.07) 0%, rgba(154,52,18,0.05) 100%)'
      : 'linear-gradient(135deg, rgba(146,64,14,0.06) 0%, rgba(217,119,6,0.08) 100%)'};
    border-radius: 12px;
    border-bottom-color: transparent;
    box-shadow: inset 0 0 0 1px ${p => p.variant === 'debate'
      ? 'rgba(55,49,81,0.2)'
      : p.variant === 'trending'
      ? 'rgba(5,150,105,0.15)'
      : 'rgba(217,119,6,0.15)'};
  }

  .item-body {
    flex: 1;
    min-width: 0;
  }

  .item-title {
    color: #1a202c;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.45;
    margin-bottom: 8px;
    letter-spacing: -0.1px;
    cursor: pointer;
    text-align: left;
    @media (max-width: 768px) {
      font-size: 13px;
    }
  }

  .item-meta {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
    font-size: 0.92em;
    color: #4a5568;
    flex-wrap: wrap;
    font-weight: 500;
  }

  .item-sigs {
    color: ${p => p.variant === 'debate' ? '#373151' : p.variant === 'trending' ? '#373151' : '#4338ca'};
    font-weight: 800;
    font-size: 13px;
  }

  .item-date {
    color: #059669;
    font-weight: 700;
    font-size: 0.75em;
    vertical-align: super;
    line-height: 1;
  }

  .item-responded {
    background: #c6f6d5;
    color: #276749;
    padding: 2px 8px;
    border-radius: 6px;
    font-size: 0.82em;
    font-weight: 700;
  }

  .item-debated {
    background: #ede9fe;
    color: #5b21b6;
    padding: 2px 8px;
    border-radius: 6px;
    font-size: 0.82em;
    font-weight: 700;
  }

  .item-debate-scheduled {
    background: #373151;
    color: #fff;
    padding: 2px 8px;
    border-radius: 6px;
    font-size: 0.82em;
    font-weight: 700;
  }

  .item-rank {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: linear-gradient(135deg, #c2410c 0%, #9a3412 100%);
    color: white;
    font-size: 0.78em;
    font-weight: 800;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 6px rgba(5, 150, 105, 0.35);
    margin-top: 1px;
  }

  .item-date-badge {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 46px;
    height: 46px;
    border-radius: 50%;
    background: #373151;
    color: white;
    flex-shrink: 0;
    box-shadow: 0 2px 8px rgba(55, 49, 81, 0.4);
    line-height: 1;
    gap: 1px;

    .badge-day {
      font-size: 1em;
      font-weight: 800;
      letter-spacing: -0.5px;
    }

    .badge-month {
      font-size: 0.58em;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      opacity: 0.85;
    }
  }

  .item-arrow {
    color: #cbd5e0;
    flex-shrink: 0;
    margin-top: 4px;
    transition: color 0.18s, transform 0.18s;
  }

  &:hover .item-arrow {
    color: ${p => p.variant === 'debate' ? '#373151' : p.variant === 'trending' ? '#c2410c' : '#667eea'};
    transform: translateX(3px);
  }
`;

const ColEmptyState = styled.div`
  color: #a0aec0;
  font-size: 0.95em;
  text-align: center;
  padding: 36px 20px;
  line-height: 1.7;
`;

const ColLoader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
  gap: 14px;

  .spinner-ring {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 3px solid #e2e8f0;
    border-top-color: ${p =>
      p.variant === 'debate' ? '#373151' :
      p.variant === 'trending' ? '#c2410c' :
      '#d97706'};
    animation: colSpin 0.7s linear infinite;
  }

  .spinner-text {
    font-size: 0.82em;
    color: #a0aec0;
    font-weight: 500;
  }

  @keyframes colSpin {
    to { transform: rotate(360deg); }
  }
`;

const CountdownText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  margin-top: 8px;

  .cd-label {
    font-size: 0.72em;
    color: #718096;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .cd-values {
    display: flex;
    align-items: baseline;
    gap: 6px;
  }

  .cd-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;

    strong {
      font-size: 1.25em;
      font-weight: 800;
      color: ${p => p.urgent ? '#ef4444' : '#373151'};
      font-variant-numeric: tabular-nums;
      letter-spacing: -0.5px;
      line-height: 1;
    }

    em {
      font-style: normal;
      font-size: 0.65em;
      font-weight: 600;
      color: #a0aec0;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }
  }

  .cd-sep {
    font-size: 1em;
    font-weight: 800;
    color: #cbd5e0;
    padding-bottom: 12px;
    line-height: 1;
  }
`;

const ColViewAllButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 11px;
  border: none;
  border-top: 1px solid #cbd5e0;
  background: #f7fafc;
  color: #4a5568;
  font-size: 0.85em;
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.15s, color 0.15s;
  letter-spacing: 0.2px;

  &:hover {
    background: #edf2f7;
    color: #2d3748;
  }

  svg {
    transition: transform 0.15s;
  }

  &:hover svg {
    transform: translateX(3px);
  }
`;


// ============= HELPER FUNCTIONS =============
const formatSignatures = (count) => {
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(0)}K`;
  return count.toLocaleString();
};

const formatDebateDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Custom hook for petition filtering and sorting
const usePetitionFilters = (petitions) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('signatures');
  const [departmentFilter, setDepartmentFilter] = useState('all');

  const filteredAndSortedPetitions = petitions
    .filter(petition => {
      const matchesSearch = petition.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        petition.creatorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        petition.departmentAcronym?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        petition.id.toString().includes(searchTerm);

      const matchesDepartment = departmentFilter === 'all' || petition.departmentAcronym === departmentFilter;

      return matchesSearch && matchesDepartment;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'signatures':
          return b.signatureCount - a.signatureCount;
        case 'date-new':
          return new Date(b.openedAt || b.createdAt) - new Date(a.openedAt || a.createdAt);
        case 'date-old':
          return new Date(a.openedAt || a.createdAt) - new Date(b.openedAt || b.createdAt);
        case 'department':
          return (a.departmentAcronym || '').localeCompare(b.departmentAcronym || '');
        case 'title':
          return a.title.localeCompare(b.title);
        default:
          return b.signatureCount - a.signatureCount;
      }
    });

  return {
    searchTerm,
    setSearchTerm,
    sortBy,
    setSortBy,
    departmentFilter,
    setDepartmentFilter,
    filteredAndSortedPetitions
  };
};

// Inject structured data into head
const injectStructuredData = (data) => {
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.text = JSON.stringify(data);
  script.id = 'petition-list-schema';

  const oldScript = document.getElementById('petition-list-schema');
  if (oldScript) {
    oldScript.remove();
  }

  document.head.appendChild(script);
};

// ============= MAIN COMPONENT =============
// Fallback only — used if /api/constituencies/popular is unreachable, so the
// section never renders empty. Live data replaces this on every load.
const FALLBACK_CONSTITUENCIES = [
  { name: 'Cities of London and Westminster', mp: 'Rachel Blake',     memberId: 5257 },
  { name: 'Manchester Central',               mp: 'Lucy Powell',      memberId: 4263 },
  { name: 'Birmingham Ladywood',              mp: 'Shabana Mahmood',  memberId: 3914 },
  { name: 'Leeds Central and Headingley',     mp: 'Alex Sobel',       memberId: 4658 },
  { name: 'Edinburgh South',                  mp: 'Ian Murray',       memberId: 3966 },
  { name: 'Cardiff South and Penarth',        mp: 'Stephen Doughty',  memberId: 4264 },
  { name: 'Bristol Central',                  mp: 'Carla Denyer',     memberId: 5102 },
  { name: 'Hackney South and Shoreditch',     mp: 'Meg Hillier',      memberId: 1524 },
  { name: 'Glasgow East',                     mp: 'John Grady',       memberId: 5170 },
  { name: 'Liverpool Riverside',              mp: 'Kim Johnson',      memberId: 4824 },
  { name: 'Islington North',                  mp: 'Jeremy Corbyn',    memberId: 185  },
  { name: 'Sheffield Hallam',                 mp: 'Olivia Blake',     memberId: 4864 },
];

const HomePage = ({ onAnalyze }) => {
  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openPetitions, setOpenPetitions] = useState([]);
  const [closedPetitions, setClosedPetitions] = useState([]);
  const [loadingPetitions, setLoadingPetitions] = useState(true);
  const [upcomingDebates, setUpcomingDebates] = useState([]);
  const [debatedPetitions, setDebatedPetitions] = useState([]);
  const [trendingNow, setTrendingNow] = useState([]);
  // petition_id -> trending rank, for the top 10 only.
  const [trendingTop10, setTrendingTop10] = useState(null);

  // One Parliament card instead of two: scheduled debates lead, and recently
  // debated petitions top it up to five so the card is never half empty.
  const PARLIAMENT_ROWS = 5;
  const TRENDING_ROWS = 8;
  const parliamentRows = useMemo(() => {
    const upcoming = (upcomingDebates || []).map(p => ({ ...p, upcoming: true }));
    const past = (debatedPetitions || []).map(p => ({ ...p, upcoming: false }));
    const seenIds = new Set(upcoming.map(p => p.id));
    const fill = past.filter(p => !seenIds.has(p.id));
    return [...upcoming, ...fill].slice(0, PARLIAMENT_ROWS);
  }, [upcomingDebates, debatedPetitions]);

  const [loadingTrending, setLoadingTrending] = useState(true);
  const [activeTab, setActiveTab] = useState('open');
  const [currentPage, setCurrentPage] = useState(1);

  // Search-related states
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [allOpenPetitions, setAllOpenPetitions] = useState([]);
  const searchTimeout = useRef(null);
  const searchContainerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Handle clearing the input
  const handleClear = () => {
    setUrl('');
    setSearchResults([]);
    setShowResults(false);
    setError(null);

    // Clear the search timeout if it exists
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
  };

  const itemsPerPage = 15;

  // Get current petitions based on active tab
  const currentPetitions = activeTab === 'open' ? openPetitions : closedPetitions;

  // Use custom hook for filtering
  const {
    searchTerm,
    setSearchTerm,
    sortBy,
    setSortBy,
    departmentFilter,
    setDepartmentFilter,
    filteredAndSortedPetitions
  } = usePetitionFilters(currentPetitions);

  // Update document meta tags
  useDocumentMeta(
    'UK Petition Tracker | Live Parliament & Government Petitions Analytics',
    'Track UK Parliament and Government petitions in real-time. Monitor live signatures, debates, responses with interactive analytics. Free petition tracking tool.'
  );

  // Lazily load all open petitions only when the user starts typing
  const searchDataLoadedRef = useRef(false);

  // Put the cursor in the search box on arrival, the way a search engine does.
  // Desktop only: on touch devices this would open the keyboard immediately
  // and hide the page before the visitor has seen it.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const coarse = window.matchMedia && window.matchMedia('(hover: none)').matches;
    if (coarse || window.innerWidth < 900) return;
    // After paint, so focusing cannot fight the browser restoring scroll.
    const id = window.requestAnimationFrame(() => {
      const el = searchInputRef.current;
      if (el && document.activeElement === document.body) el.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(id);
  }, []);

  const allOpenPetitionsRef = useRef([]);

  const loadSearchData = useCallback(async () => {
    if (searchDataLoadedRef.current) return allOpenPetitionsRef.current;
    searchDataLoadedRef.current = true;
    try {
      const data = await fetchAllOpenPetitions();
      if (data) {
        allOpenPetitionsRef.current = data;
        setAllOpenPetitions(data);
      }
      return allOpenPetitionsRef.current;
    } catch (error) {
      searchDataLoadedRef.current = false; // allow retry
      return [];
    }
  }, []);

  // Handle click outside to close search results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load petitions
  const loadPetitions = useCallback(async (forceRefresh = false) => {
    try {
      setLoadingPetitions(true);

      const [debateData, popularData] = await Promise.all([
        fetchHomeDebates(),
        fetchHomePopular(),
      ]);

            // Recently debated: independent of the others, so a failure here must
      // not block the rest of the homepage.
      fetchHomeDebated(5).then(setDebatedPetitions).catch(() => {});

      const validDebates = (debateData || []).filter(p => {
        const target = getDebateTarget(p.scheduledDebateDate);
        return target && (target - Date.now()) > -LIVE_WINDOW;
      });
      setUpcomingDebates(validDebates);
      setOpenPetitions(popularData || []);
    } catch (error) {
      console.error('Failed to load petitions:', error);
      setOpenPetitions([]);
    } finally {
      setLoadingPetitions(false);
    }
  }, []);

  useEffect(() => {
    loadPetitions(false);
  }, [loadPetitions]);

  // Constituencies ranked by real signature totals, with MP portraits resolved
  // by name. Falls back to the static list if the API is unreachable.
  const [constituencies, setConstituencies] = useState(FALLBACK_CONSTITUENCIES);


  // Headline totals from the backend, which computes them from the full open
  // petitions list. Deliberately not derived from the client's lazily-loaded
  // search data, which holds only the displayed subset at first paint.
  const [heroStats, setHeroStats] = useState({ openPetitions: 0, totalSignatures: 0, debatesScheduled: 0 });

  useEffect(() => {
    let cancelled = false;

    // A partial response means the backend cache was cold and it only saw
    // page 1, which would show a badly wrong count. Retry briefly instead of
    // rendering it.
    const load = async (attempt = 0) => {
      const stats = await fetchHomeStats();
      if (cancelled) return;
      if (stats && !stats.partial) { setHeroStats(stats); return; }
      // Retry quickly at first — the backend usually has complete figures
      // within a second or two, and a long gap leaves placeholders on screen.
      if (attempt < 6) setTimeout(() => load(attempt + 1), 600 + attempt * 400);
    };

    load();
    return () => { cancelled = true; };
  }, []);
  const [mpPhotos, setMpPhotos] = useState({});

  useEffect(() => {
    let cancelled = false;

    const loadConstituencies = async () => {
      const [ranked, photos] = await Promise.all([
        fetchPopularConstituencies(12),
        getMpPhotos().catch(() => ({}))
      ]);
      if (cancelled) return;
      setMpPhotos(photos || {});
      if (ranked && ranked.length) setConstituencies(ranked);
    };

    loadConstituencies();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const loadTrending = async () => {
      const urls = process.env.NODE_ENV === 'production'
        ? ['https://timedata.ukpetitiontracker.com']
        : ['http://localhost:5001', 'https://timedata.ukpetitiontracker.com'];
      for (const base of urls) {
        try {
          const res = await fetch(`${base}/trending?hours=2`);
          if (!res.ok) continue;
          const data = await res.json();
          const petitions = data.trending_petitions || [];
          if (petitions.length > 0) {
            setTrendingNow(petitions.slice(0, TRENDING_ROWS));
            // The card shows 8, but the live ticker marks anything in the
            // top 10, so keep those ranks rather than refetching there.
            setTrendingTop10(new Map(
              petitions.slice(0, 10).map((p, i) => [String(p.petition_id), p.rank || i + 1]),
            ));
            setLoadingTrending(false);
            return;
          }
        } catch {
          // try next url
        }
      }
      setTrendingNow([]);
      setTrendingTop10(null);
      setLoadingTrending(false);
    };
    loadTrending();
  }, []);

  // Reset to page 1 when filters or tab change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortBy, departmentFilter, activeTab]);

  // Search function — optionally accepts a pre-loaded data array
  const searchPetitions = (query, dataOverride) => {
    const searchTerm = query.toLowerCase().trim();

    if (!searchTerm || searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }

    // Check if it's a URL or ID pattern
    const petitionId = extractPetitionId(searchTerm);
    if (petitionId || /^\d+$/.test(searchTerm)) {
      // If it looks like an ID or URL, don't show search results
      setSearchResults([]);
      return;
    }

    // Use override data (from hint chip) or fall back to state
    const pool = dataOverride || allOpenPetitionsRef.current || allOpenPetitions;

    // Search through petition titles and other fields
    const results = pool
      .filter(petition => {
        const titleMatch = petition.title.toLowerCase().includes(searchTerm);
        const creatorMatch = petition.creatorName?.toLowerCase().includes(searchTerm);
        const deptMatch = petition.departmentAcronym?.toLowerCase().includes(searchTerm);
        const idMatch = petition.id.toString().includes(searchTerm);

        return titleMatch || creatorMatch || deptMatch || idMatch;
      })
      .sort((a, b) => {
        // Prioritize title matches
        const aTitle = a.title.toLowerCase();
        const bTitle = b.title.toLowerCase();
        const aStartsWith = aTitle.startsWith(searchTerm);
        const bStartsWith = bTitle.startsWith(searchTerm);

        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;

        // Then sort by signature count
        return b.signatureCount - a.signatureCount;
      })
      .slice(0, 10); // Limit to top 10 results

    setSearchResults(results);
    setShowResults(results.length > 0);
  };

  // Handle input change with debounced search
  const handleInputChange = (e) => {
    const value = e.target.value;
    setUrl(value);

    // Clear previous timeout
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    // Set searching state and ensure search data is loaded
    if (value.trim().length >= 2) {
      setSearching(true);
      loadSearchData();
    }

    // Debounce search
    searchTimeout.current = setTimeout(() => {
      searchPetitions(value);
      setSearching(false);
    }, 300);
  };

  // Handle search result selection with context-aware UTM tracking
  const handleResultClick = (petition, resultIndex) => {
    const petitionId = extractPetitionId(petition.url);
    if (petitionId) {
      setShowResults(false);
      setSearchResults([]);

      // Create detailed UTM parameters
      const utmParams = new URLSearchParams({
        utm_source: 'internal_search',
        utm_medium: 'autocomplete',
        utm_campaign: 'petition_discovery',
        utm_term: encodeURIComponent(url.trim()),
      });

      navigate(`/petition/${petitionId}/${slugify(petition.title)}?${utmParams.toString()}`);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!url.trim() || loading) return;

    setLoading(true);
    setError(null);
    setShowResults(false); // Hide search results when submitting

    try {
      const petitionId = extractPetitionId(url.trim());

      if (!petitionId) {
        throw new Error('Invalid petition Name or URL or ID format. Please enter a valid petition URL or ID (e.g., 727360)');
      }

      try {
        const petitionUrl = buildPetitionUrl(petitionId);
        const petitionData = await fetchPetitionData(petitionUrl);
        navigate(`/petition/${petitionId}/${slugify(petitionData.title)}`);
      } catch (fetchError) {
        throw new Error(`Petition ${petitionId} not found. Please check the ID and try again.`);
      }
    } catch (err) {
      console.error('Navigation error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle petition card click
  const handlePetitionClick = useCallback((petition) => {
    const petitionId = extractPetitionId(petition.url);
    if (petitionId) {
      navigate(`/petition/${petitionId}/${slugify(petition.title)}`);
    }
  }, [navigate]);

  // Handle View All button clicks
  const handleViewAll = () => {
    if (activeTab === 'open') {
      navigate('/petitions/open');
    } else {
      navigate('/petitions/closed');
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredAndSortedPetitions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPagePetitions = filteredAndSortedPetitions.slice(startIndex, endIndex);

  // Update structured data when petitions change
  useEffect(() => {
    if (currentPagePetitions.length > 0) {
      const petitionListSchema = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": `${activeTab === 'open' ? 'Open' : 'Closed'} UK Parliament Petitions`,
        "numberOfItems": currentPagePetitions.length,
        "itemListElement": currentPagePetitions.map((petition, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "url": `https://ukpetitiontracker.com/petition/${extractPetitionId(petition.url)}/${slugify(petition.title)}`,
          "name": petition.title,
          "item": {
            "@type": "Dataset",
            "name": petition.title,
            "description": `${petition.title} — UK Parliament petition with ${petition.signatureCount.toLocaleString()} signatures.${petition.background ? ' ' + petition.background.substring(0, 150) : ''}`.trim(),
            "dateCreated": petition.createdAt,
            "license": "https://www.parliament.uk/site-information/copyright-parliament.uk/",
            "creator": {
              "@type": "Organization",
              "name": "UK Parliament",
              "url": "https://petition.parliament.uk"
            }
          }
        }))
      };

      injectStructuredData(petitionListSchema);
    }
  }, [currentPagePetitions, activeTab]);

  // Get unique departments
  const uniqueDepartments = [...new Set(currentPetitions.map(p => p.departmentAcronym))].filter(Boolean);

  return (
    <PageWrapper>
      <Header />

      <HomeContainer>
        <PageHeader>
          <PageTitle>
            Track UK Parliament Petitions
          </PageTitle>
          <PageSubtitle>
            See how fast a petition is growing, where its support comes from, and what Parliament does next.
          </PageSubtitle>
        </PageHeader>

        {/* Enhanced Search Form */}
        <SearchContainer onSubmit={handleSubmit} ref={searchContainerRef}>
          <InputWrapper>
            <SearchIcon><Search size={18} style={{ color: '#999' }} /></SearchIcon>
            <Input
              ref={searchInputRef}
              type="text"
              value={url}
              onChange={handleInputChange}
              placeholder="Search by keyword, paste a URL or enter a petition ID…"
              disabled={loading}
              aria-label="Search for UK Parliament petition"
              autoComplete="off"
            />
            <ClearButton
              type="button"
              show={url.length > 0}
              onClick={handleClear}
              aria-label="Clear search"
              tabIndex={url.length > 0 ? 0 : -1}
            >
              ×
            </ClearButton>
            <SubmitButton type="submit" disabled={loading || !url.trim()}>
              {loading ? (
                <>
                  <span>Loading...</span>
                </>
              ) : (
                <span>Track</span>
              )}
            </SubmitButton>
          </InputWrapper>

          {showResults && (
            <SearchResults>
              {searching ? (
                <SearchingIndicator>
                  <span className="spinner"></span>
                  Searching petitions...
                </SearchingIndicator>
              ) : searchResults.length > 0 ? (
                searchResults.map((petition) => (
                  <ResultItem
                    key={petition.id}
                    to={buildAppPetitionUrl(petition.id, petition.title)}
                  >
                    <div className="title">{petition.title}</div>
                    <div className="meta">
                      <span className="signatures">
                        {formatSignatures(petition.signatureCount)} signatures
                      </span>
                      {petition.hasGovernmentResponse && (
                        <span className="status responded">Responded</span>
                      )}
                      {petition.hasDebate && (
                        <span className="status debated">Debated</span>
                      )}
                      {petition.departmentAcronym && (
                        <span className="department">{petition.departmentAcronym}</span>
                      )}
                    </div>
                  </ResultItem>
                ))
              ) : (
                <NoResults>
                  No petitions found matching "{url}"
                </NoResults>
              )}
            </SearchResults>
          )}
        </SearchContainer>

        {/* Search hint chips — outside form so dropdown overlaps them */}
        <SearchHints>
          <HintLabel>Try:</HintLabel>
          {['NHS', 'Climate', 'Housing', 'Voting', 'Immigration', 'Education', 'ILR'].map(kw => (
            <HintChip key={kw} type="button" onClick={async () => {
              setUrl(kw);
              setSearching(true);
              const data = await loadSearchData();
              searchPetitions(kw, data);
              setSearching(false);
            }}>{kw}</HintChip>
          ))}
        </SearchHints>

        {/* Hero stats strip */}
        <HeroStats>
          <HeroStatLink to="/petitions/recently-opened" title="See the newest open petitions">
            <div className="hs-val">
              {heroStats.openPetitions ? heroStats.openPetitions.toLocaleString() : '—'}
              <DeltaChip
                $zero={!heroStats.openedToday}
                title={`${heroStats.openedToday || 0} new petitions opened today`}
              >
                +{heroStats.openedToday || 0} today
              </DeltaChip>
            </div>
            <div className="hs-lbl">Open petitions</div>
          </HeroStatLink>
          <HeroStatLink to="/petitions/closed" title="Browse closed petitions">
            <div className="hs-val">
              {heroStats.closedTotal ? heroStats.closedTotal.toLocaleString() : '—'}
              <DeltaChip
                $zero={!heroStats.closedToday}
                title={`${heroStats.closedToday || 0} petitions closed today`}
              >
                +{heroStats.closedToday || 0} today
              </DeltaChip>
            </div>
            <div className="hs-lbl">Closed petitions</div>
          </HeroStatLink>
          <HeroStatLink to="/petitions/rejected" title="Browse rejected petitions">
            <div className="hs-val">
              {heroStats.rejectedTotal ? heroStats.rejectedTotal.toLocaleString() : '—'}
              <DeltaChip
                $zero={!heroStats.rejectedToday}
                title={`${heroStats.rejectedToday || 0} petitions rejected today`}
              >
                +{heroStats.rejectedToday || 0} today
              </DeltaChip>
            </div>
            <div className="hs-lbl">Rejected petitions</div>
          </HeroStatLink>
          <HeroStatLink to="/petitions/milestones" title="Petitions close to 10,000 or 100,000 signatures">
            <div className="hs-val">
              {heroStats.nearMilestones ? heroStats.nearMilestones.toLocaleString() : '—'}
            </div>
            <div className="hs-lbl">Near milestones</div>
          </HeroStatLink>
          <HeroStatLink to="/petitions/debate-scheduled" title="See every scheduled debate">
            <div className="hs-val">{heroStats.debatesScheduled || upcomingDebates.length || '—'}</div>
            <div className="hs-lbl">Debates scheduled</div>
          </HeroStatLink>
        </HeroStats>

        <TwoColSection>
          <LiveActivity asCard trendingRanks={trendingTop10} />

          {/* Trending Now */}
          <ColCard>
            <ColCardHeader variant="trending">
              <TrendingUp />
              <div>
                <h3>Trending Now</h3>
                <div className="col-sub">Fastest growing last 2 hours</div>
              </div>
            </ColCardHeader>
            <ColCardBody>
              {loadingTrending ? (
                <ColLoader variant="trending">
                  <div className="spinner-ring" />
                  <span className="spinner-text">Loading trends...</span>
                </ColLoader>
              ) : trendingNow.length === 0 ? (
                <ColEmptyState>No trending data available.</ColEmptyState>
              ) : (
                trendingNow.map((p, i) => (
                  <MiniPetitionItem key={p.petition_id} variant="trending"
                    to={`/petition/${p.petition_id}/${slugify(p.title)}`}
                  >
                    <span className="item-rank">{i + 1}</span>
                    <div className="item-body">
                      <div className="item-title">{p.title}</div>
                      <div className="item-meta">
                        <RankMove $change={p.rank_change} />
                        <span className="item-sigs">{(p.current_signatures || 0).toLocaleString()} signatures</span>
                        <span className="item-date">+{(p.signature_growth || 0).toLocaleString()} · 2h</span>
                        {p.signatures_per_hour > 0 && (
                          <span style={{ background: '#dcfce7', color: '#15803d', padding: '1px 7px', borderRadius: 5, fontSize: '0.78em', fontWeight: 700 }}>
                            {p.signatures_per_hour >= 1000
                              ? `${(p.signatures_per_hour/1000).toFixed(1)}K/hr`
                              : `${Math.round(p.signatures_per_hour)}/hr`}
                          </span>
                        )}
                      </div>
                    </div>
                  </MiniPetitionItem>
                ))
              )}
            </ColCardBody>
            <ColViewAllButton to="/petitions/trending">
              View All Trending <ArrowRight size={14} />
            </ColViewAllButton>
          </ColCard>

          {/* Parliament — upcoming debates first, then recent ones to fill
              the card. Five rows total: if only three are scheduled, two
              recently debated petitions make up the difference. */}
          <ColCard>
            <ColCardHeader variant="debate">
              <Landmark />
              <div>
                <h3>In Parliament</h3>
                <div className="col-sub">Debates scheduled and heard</div>
              </div>
              {parliamentRows.length > 0 && (
                <span className="col-badge">
                  {upcomingDebates.length > 0
                    ? `${upcomingDebates.length} scheduled`
                    : `Last ${parliamentRows.length}`}
                </span>
              )}
            </ColCardHeader>
            <ColCardBody>
              {loadingPetitions && parliamentRows.length === 0 ? (
                <ColLoader variant="debate">
                  <div className="spinner-ring" />
                  <span className="spinner-text">Loading debates...</span>
                </ColLoader>
              ) : parliamentRows.length === 0 ? (
                <ColEmptyState>No debates to show.<br />Check back soon.</ColEmptyState>
              ) : (
                parliamentRows.map((p) => {
                  const on = p.upcoming
                    ? p.scheduledDebateDate
                    : (p.debatedOn || p.scheduledDebateDate);
                  // Parliament sometimes repeats the transcript URL as the
                  // debate pack, so the duplicate is dropped.
                  const pack = p.debatePackUrl && p.debatePackUrl !== p.debateTranscriptUrl
                    ? p.debatePackUrl : null;
                  const links = p.upcoming ? [] : [
                    p.debateTranscriptUrl && ['Transcript', p.debateTranscriptUrl],
                    p.debateVideoUrl && ['Watch', p.debateVideoUrl],
                    pack && ['Debate pack', pack],
                  ].filter(Boolean);

                  return (
                    <DebatedItem key={`${p.upcoming ? 'u' : 'd'}-${p.id}`}>
                      <MiniPetitionItem variant="debate"
                        to={`/petition/${p.id}/${slugify(p.title)}`}
                      >
                        <div className="item-date-badge">
                          <span className="badge-day">{new Date(on).getDate()}</span>
                          <span className="badge-month">
                            {new Date(on).toLocaleString('en-GB', { month: 'short' })}
                          </span>
                        </div>
                        <div className="item-body">
                          <div className="item-title">{p.title}</div>
                          <div className="item-meta">
                            <span className="item-sigs">
                              {(p.signatureCount || 0).toLocaleString()} signatures
                            </span>
                            {/* Without this the two kinds are indistinguishable
                                once they share a card. */}
                            <PhaseTag $upcoming={p.upcoming}>
                              {p.upcoming ? 'Scheduled' : 'Debated'}
                            </PhaseTag>
                          </div>
                          {p.upcoming && <MiniCountdown dateStr={p.scheduledDebateDate} />}
                        </div>
                      </MiniPetitionItem>
                      {links.length > 0 && (
                        <DebateLinks>
                          {links.map(([label, href]) => (
                            <a key={label} href={href} target="_blank" rel="noopener noreferrer">
                              {label}
                              <ExternalLink size={11} aria-hidden="true" />
                            </a>
                          ))}
                        </DebateLinks>
                      )}
                    </DebatedItem>
                  );
                })
              )}
            </ColCardBody>
            <ColViewAllButton to={upcomingDebates.length > 0 ? '/petitions/debate-scheduled' : '/petitions/debated'}>
              View All Debates <ArrowRight size={14} />
            </ColViewAllButton>
          </ColCard>

          {/* Popular Petitions — mobile only, see MobileOnlyColCard */}
          <MobileOnlyColCard>
          <ColCard>
            <ColCardHeader variant="popular">
              <Award />
              <div>
                <h3>Popular Petitions</h3>
                <div className="col-sub">Open · Most signatures</div>
              </div>
              <span className="col-badge">Top 7</span>
            </ColCardHeader>
            <ColCardBody>
              {loadingPetitions ? (
                <ColLoader variant="popular">
                  <div className="spinner-ring" />
                  <span className="spinner-text">Loading petitions...</span>
                </ColLoader>
              ) : openPetitions.length === 0 ? (
                <ColEmptyState>No data available.</ColEmptyState>
              ) : (
                openPetitions.slice(0, 7).map((p, i) => (
                  <MiniPetitionItem key={p.id} variant="popular"
                    to={`/petition/${p.id}/${slugify(p.title)}`}
                  >
                    <span className="item-rank" style={{ background: 'linear-gradient(135deg, #c2410c 0%, #9a3412 100%)', boxShadow: '0 2px 6px rgba(154,52,18,0.3)' }}>{i + 1}</span>
                    <div className="item-body">
                      <div className="item-title">{p.title}</div>
                      <div className="item-meta">
                        <span className="item-sigs">{(p.signatureCount || 0).toLocaleString()} signatures</span>
                        {p.hasGovernmentResponse && <span className="item-responded">Responded</span>}
                        {p.scheduledDebateDate && new Date(p.scheduledDebateDate) >= new Date()
                          ? <span className="item-debate-scheduled">Debate Scheduled</span>
                          : p.hasDebate && <span className="item-debated">Debated</span>
                        }
                      </div>
                    </div>
                  </MiniPetitionItem>
                ))
              )}
            </ColCardBody>
            <ColViewAllButton to="/petitions/open">
              View All Open Petitions <ArrowRight size={14} />
            </ColViewAllButton>
          </ColCard>
          </MobileOnlyColCard>
        </TwoColSection>


        {/* Popular constituencies */}
        <ConstituencySection>
          <ConstituencySectionHeader>
            <h3>Popular Constituencies</h3>
            <p className="cs-sub">Ranked by signatures on the current top petitions</p>
          </ConstituencySectionHeader>
          <ConstituencyGrid>
            {constituencies.map(c => {
              const fallback = `https://ui-avatars.com/api/?name=${encodeURIComponent(c.mp || c.name)}&size=46&background=302b63&color=fff&rounded=true`;
              // Live entries resolve portraits by MP name; the static fallback
              // list carries a memberId instead.
              // Names arrive titled ("Rt Hon Mark Francois MP"); the map is
              // keyed on the bare name, so normalise before looking up.
              const photoSrc = mpPhotos[normaliseMpName(c.mp)] || mpPhotos[c.mp]
                || (c.memberId ? `https://members-api.parliament.uk/api/Members/${c.memberId}/Thumbnail` : fallback);
              return (
                <ConstituencyCard key={c.name} to={`/petitions/local/${slugify(c.name)}`}>
                  <img src={photoSrc} alt={c.mp} onError={e => { e.target.src = fallback; e.target.onerror = null; }} />
                  <div className="cc-name">{c.name}</div>
                  <div className="cc-mp">MP: {c.mp}</div>
                </ConstituencyCard>
              );
            })}
          </ConstituencyGrid>
          <div style={{ textAlign: 'center' }}>
            <ConstituencyExploreBtn to="/petitions/local">
              Explore All Constituencies <ArrowRight size={13} />
            </ConstituencyExploreBtn>
          </div>
        </ConstituencySection>

        {error && (
          <ErrorMessage role="alert">
            {error}
          </ErrorMessage>
        )}


        {/* Hidden SEO content for search engines */}
        <SEOContent>
          <h2>UK Government and Parliament Petitions Tracker</h2>
          <p>Track UK petitions, monitor parliament petitions UK, view government petition responses, analyze petition signatures live.</p>
          <h3>Features of UK Petition Tracker</h3>
          <ul>
            <li>Real-time UK petition signatures tracking</li>
            <li>Parliament petition debates monitoring</li>
            <li>Government petition responses tracker</li>
            <li>UK petition analytics and statistics</li>
            <li>Trending UK petitions discovery</li>
            <li>Historical petition data analysis</li>
          </ul>
          <h3>Popular UK Petition Categories</h3>
          <p>Brexit petitions, NHS petitions, Education petitions, Environment petitions, Justice petitions, Economy petitions</p>
        </SEOContent>
      </HomeContainer>
    </PageWrapper>
  );
};

export default HomePage;